// Lightweight A/B variant router for the Calmasutras landing page.
//
// How a visitor gets assigned to a variant (in order of precedence):
//   1. ?aud=v2 (or ?aud=default) query param           [explicit, no-indexed]
//   2. localStorage("cs_aud") set on a prior visit     [sticky for the user]
//   3. 50/50 random coin flip                          [first-time visitor]
//
// Side effects on mount:
//   - Persists the chosen variant to localStorage.
//   - Exposes window.__CS_AUD__ so axios bodies + gtag events can attach it.
//   - Sets a gtag user_property `audience_variant` once gtag exists.
//   - When the variant came in via ?aud= URL, adds <meta name="robots"
//     content="noindex,nofollow"> so variant URLs don't compete in SEO.

import { createContext, useContext, useEffect, useMemo, useState } from "react";
import {
  COPY_VARIANTS,
  VARIANT_DEFAULT,
  VARIANT_V2,
  resolveCopy,
} from "@/data/copyVariants";

const STORAGE_KEY = "cs_aud";
const VALID = new Set(Object.keys(COPY_VARIANTS));

function readUrlVariant() {
  if (typeof window === "undefined") return null;
  try {
    const p = new URLSearchParams(window.location.search).get("aud");
    if (p && VALID.has(p)) return p;
  } catch (_) { /* noop */ }
  return null;
}

function readStoredVariant() {
  if (typeof window === "undefined") return null;
  try {
    const v = window.localStorage.getItem(STORAGE_KEY);
    if (v && VALID.has(v)) return v;
  } catch (_) { /* noop */ }
  return null;
}

function pickRandom() {
  // 50/50 between default and v2 for now. Add more variants later by
  // expanding COPY_VARIANTS and updating the bucket math.
  return Math.random() < 0.5 ? VARIANT_DEFAULT : VARIANT_V2;
}

function resolveInitialVariant() {
  const urlV = readUrlVariant();
  if (urlV) return { variant: urlV, source: "url" };
  const stored = readStoredVariant();
  if (stored) return { variant: stored, source: "storage" };
  return { variant: pickRandom(), source: "random" };
}

function applyNoindexIfNeeded(source) {
  if (typeof document === "undefined") return;
  // Only inject noindex when the user landed via an explicit ?aud= link.
  // Random-bucketed visitors still see the canonical URL with default SEO.
  if (source !== "url") return;
  let tag = document.querySelector('meta[name="robots"]');
  if (!tag) {
    tag = document.createElement("meta");
    tag.setAttribute("name", "robots");
    document.head.appendChild(tag);
  }
  tag.setAttribute("content", "noindex,nofollow");
}

function pushToAnalytics(variant) {
  if (typeof window === "undefined") return;
  window.__CS_AUD__ = variant;
  try {
    if (typeof window.gtag === "function") {
      // Custom dimension - configure `audience_variant` in GA4 to use it as a
      // segment / report breakdown. Until then the value is still attached to
      // every event payload below.
      window.gtag("set", "user_properties", { audience_variant: variant });
      window.gtag("event", "experiment_impression", {
        experiment_id: "landing_v2",
        variant_id: variant,
        audience_variant: variant,
      });
    }
  } catch (_) { /* noop */ }
}

const CopyContext = createContext({
  variant: VARIANT_DEFAULT,
  source: "default",
  c: (key) => resolveCopy(VARIANT_DEFAULT, key),
});

export function CopyVariantProvider({ children }) {
  const [{ variant, source }, setState] = useState(() => ({
    variant: VARIANT_DEFAULT,
    source: "default",
  }));

  // Resolve once on the client. We never re-flip after assignment to avoid a
  // mid-session UX change.
  useEffect(() => {
    const picked = resolveInitialVariant();
    setState(picked);
    try {
      window.localStorage.setItem(STORAGE_KEY, picked.variant);
    } catch (_) { /* noop */ }
    applyNoindexIfNeeded(picked.source);
    pushToAnalytics(picked.variant);
  }, []);

  const value = useMemo(
    () => ({
      variant,
      source,
      c: (key) => resolveCopy(variant, key),
    }),
    [variant, source]
  );

  return <CopyContext.Provider value={value}>{children}</CopyContext.Provider>;
}

export function useCopy() {
  return useContext(CopyContext);
}

// Synchronous accessor for non-React code (e.g. axios interceptors).
export function getCurrentVariant() {
  if (typeof window === "undefined") return VARIANT_DEFAULT;
  return window.__CS_AUD__ || readStoredVariant() || VARIANT_DEFAULT;
}

export { VARIANT_DEFAULT, VARIANT_V2 };
